/**
 * 触觉与听觉反馈。
 *
 * 设计立场：只给【结果】，不给【输入】。
 *
 * 妈妈在柜台用，顾客就站在面前。每点一下都响一声，在顾客眼里像在玩游戏，
 * 而且很快会烦。所以导航、开抽屉、打字一律不响 ——
 * 只有「事情成了」的时刻才出声：完成一步、收到钱、开完单。
 *
 * 这类声音的真正价值在于：她低头写字、手上还沾着石膏的时候，
 * 不用抬头看屏幕也知道刚才那下点成了。
 *
 * 用 Web Audio 合成而不是加载音频文件：三个音效不值得多几十 KB 和几个网络请求，
 * 而且合成的音量、时长完全可控，不会某天冒出一个刺耳的采样。
 *
 * 开关存在本机而不是账号上 —— 这是「这台设备放在什么环境里」的属性：
 * 柜台平板可能要静音，妈妈自己手机上可能想要。
 */

const SOUND_KEY = 'gb.sound'
const HAPTIC_KEY = 'gb.haptic'

export function soundEnabled(): boolean {
  return localStorage.getItem(SOUND_KEY) !== 'off'
}
export function setSoundEnabled(on: boolean): void {
  localStorage.setItem(SOUND_KEY, on ? 'on' : 'off')
}
export function hapticEnabled(): boolean {
  return localStorage.getItem(HAPTIC_KEY) !== 'off'
}
export function setHapticEnabled(on: boolean): void {
  localStorage.setItem(HAPTIC_KEY, on ? 'on' : 'off')
}

let ctx: AudioContext | null = null

/** 浏览器要求先有用户手势才能出声。音效都挂在点击后，天然满足。 */
function audio(): AudioContext | null {
  if (typeof window === 'undefined') return null
  try {
    ctx ??= new (window.AudioContext ?? (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)()
    if (ctx.state === 'suspended') void ctx.resume()
    return ctx
  } catch {
    return null
  }
}

/**
 * 一个带包络的正弦音。
 * 关键是 attack/release 要柔 —— 方波或者硬切会变成廉价的「哔」。
 */
function tone(freq: number, startAt: number, durMs: number, peak: number): void {
  const ac = audio()
  if (!ac) return

  const osc = ac.createOscillator()
  const gain = ac.createGain()
  osc.type = 'sine'
  osc.frequency.value = freq

  const t0 = ac.currentTime + startAt / 1000
  const dur = durMs / 1000
  gain.gain.setValueAtTime(0, t0)
  gain.gain.linearRampToValueAtTime(peak, t0 + 0.012) // 12ms 起音，不刺耳
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)

  osc.connect(gain).connect(ac.destination)
  osc.start(t0)
  osc.stop(t0 + dur + 0.02)
}

/** 音量刻意压得很低：柜台是安静环境，够听见就行，不该盖过说话声。 */
const VOL = 0.06

const SOUNDS = {
  /** 完成一步 —— 上行纯五度，干净利落的「成了」 */
  done: () => {
    tone(784, 0, 90, VOL) // G5
    tone(1175, 60, 140, VOL * 0.85) // D6
  },
  /** 收到钱 —— 三个音的小上行，比 done 更「圆满」一点 */
  money: () => {
    tone(659, 0, 80, VOL)
    tone(784, 55, 80, VOL)
    tone(1047, 110, 180, VOL * 0.9)
  },
  /** 出错 —— 下行小三度，低沉但不刺耳。不用蜂鸣，那会吓到顾客 */
  error: () => {
    tone(392, 0, 110, VOL * 0.8)
    tone(311, 90, 180, VOL * 0.8)
  },
} as const

export type SoundName = keyof typeof SOUNDS

const HAPTICS: Record<SoundName, number | number[]> = {
  done: 18,
  money: [14, 40, 22],
  error: [30, 50, 30],
}

/**
 * 触发一次反馈。
 * 手机上振动比声音更有用 —— 在店里嘈杂环境下听不见，但手能感觉到。
 */
export function feedback(name: SoundName): void {
  if (soundEnabled()) {
    try {
      SOUNDS[name]()
    } catch {
      /* 浏览器不支持或被策略挡了，静默降级 —— 反馈失败不该影响业务 */
    }
  }
  if (hapticEnabled() && typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    try {
      navigator.vibrate(HAPTICS[name])
    } catch {
      /* 同上 */
    }
  }
}
